import React, { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, Calendar, BarChart3, TrendingUp, CreditCard, User, Settings, Download, FileText, CheckCircle } from 'lucide-react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency } from '../../utils/formatters';
import { PaymentCalculatorState, PaymentData } from './types';
import { calculatePaymentData } from './utils/calculations';
import ConfigurationTab from './tabs/ConfigurationTab';
import DownPaymentScheduleTab from './tabs/DownPaymentScheduleTab';
import AmortizationTab from './tabs/AmortizationTab';
import BuybackPricesTab from './tabs/BuybackPricesTab';
import ReturnsSimulatorTab from './tabs/ReturnsSimulatorTab';

interface PaymentCalculatorMainProps {
  onBack: () => void;
}

const PaymentCalculatorMain: React.FC<PaymentCalculatorMainProps> = ({ onBack }) => {
  const { investment, updateInvestment } = useCalculator();
  
  const [state, setState] = useState<PaymentCalculatorState>({
    activeTab: 'configuration',
    investorName: '',
    investorPhone: '',
    investorEmail: '',
    executiveName: '',
    executivePhone: '',
    executiveEmail: '',
    downloadFormat: 'html',
    downloadStatus: 'idle'
  });

  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);

  // Check if client information is complete
  const isClientInfoComplete = () => {
    return state.investorName.trim() !== '' && 
           state.investorPhone.trim() !== '' && 
           state.investorEmail.trim() !== '';
  };

  // Calculate payment data when investment changes
  useEffect(() => {
    try {
      const data = calculatePaymentData(investment);
      setPaymentData(data);
    } catch (error) {
      console.error('Error calculating payment data:', error);
    }
  }, [investment]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStateUpdate = (updates: Partial<PaymentCalculatorState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const generateCompleteReport = () => {
    setState(prev => ({ ...prev, downloadStatus: 'downloading' }));
    
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const totalInvestment = investment.initialCertificates * investment.certificateBasePrice;
    const downPaymentAmount = totalInvestment * (investment.downPaymentPercentage || 30) / 100;

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Reporte Completo de Pagos - Cosecha Capital</title>
        <style>
          :root {
            --primary: #16a34a;
            --primary-light: #22c55e;
            --secondary: #0284c7;
            --gray-100: #f3f4f6;
            --gray-200: #e5e7eb;
            --gray-700: #374151;
            --gray-800: #1f2937;
          }
          
          body {
            font-family: system-ui, -apple-system, sans-serif;
            line-height: 1.6;
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px;
            color: var(--gray-800);
            background: #ffffff;
          }
          
          .header {
            text-align: center;
            margin-bottom: 60px;
            padding-bottom: 30px;
            border-bottom: 3px solid var(--primary);
          }
          
          .header h1 {
            color: var(--primary);
            font-size: 2.5em;
            margin-bottom: 10px;
          }
          
          .section {
            margin-bottom: 40px;
            padding: 30px;
            background: var(--gray-100);
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          
          .section-title {
            color: var(--primary);
            font-size: 1.5em;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid var(--primary-light);
          }
          
          .grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .stat-card {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            background: white;
            border-radius: 8px;
            overflow: hidden;
          }
          
          th, td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid var(--gray-200);
          }
          
          th {
            background: var(--primary);
            color: white;
            font-weight: 500;
          }
          
          tr:nth-child(even) {
            background: var(--gray-100);
          }
          
          .footer {
            text-align: center;
            margin-top: 60px;
            padding-top: 20px;
            border-top: 2px solid var(--gray-200);
            color: var(--gray-700);
          }
          
          .client-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
          }
          
          .highlight {
            background: #dcfce7 !important;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Reporte Completo de Pagos</h1>
          <h2>Cosecha Capital</h2>
          <p>Generado el ${formattedDate}</p>
        </div>
        
        <div class="section">
          <h2 class="section-title">Información del Cliente</h2>
          <div class="client-info">
            <div class="stat-card">
              <h3>Datos del Inversionista</h3>
              <p><strong>Nombre:</strong> ${state.investorName || 'Sin especificar'}</p>
              <p><strong>Teléfono:</strong> ${state.investorPhone || 'Sin especificar'}</p>
              <p><strong>Email:</strong> ${state.investorEmail || 'Sin especificar'}</p>
            </div>
            <div class="stat-card">
              <h3>Ejecutivo Asignado</h3>
              <p><strong>Nombre:</strong> ${state.executiveName || 'Sin especificar'}</p>
              <p><strong>Teléfono:</strong> ${state.executivePhone || 'Sin especificar'}</p>
              <p><strong>Email:</strong> ${state.executiveEmail || 'Sin especificar'}</p>
            </div>
          </div>
        </div>

        <div class="section">
          <h2 class="section-title">Resumen de Inversión</h2>
          <div class="grid">
            <div class="stat-card">
              <h3>Certificados</h3>
              <p>${investment.initialCertificates}</p>
            </div>
            <div class="stat-card">
              <h3>Inversión Total</h3>
              <p>${formatCurrency(totalInvestment, investment.currencyFormat)}</p>
            </div>
            <div class="stat-card">
              <h3>Enganche (${investment.downPaymentPercentage || 30}%)</h3>
              <p>${formatCurrency(downPaymentAmount, investment.currencyFormat)}</p>
            </div>
            <div class="stat-card">
              <h3>A Financiar</h3>
              <p>${formatCurrency(paymentData?.totalFinanced || 0, investment.currencyFormat)}</p>
            </div>
            <div class="stat-card">
              <h3>Mensualidad</h3>
              <p>${formatCurrency((paymentData?.totalFinanced || 0) / 48, investment.currencyFormat)}</p>
            </div>
            <div class="stat-card">
              <h3>Interés Total</h3>
              <p>${formatCurrency(paymentData?.totalInterest || 0, investment.currencyFormat)}</p>
            </div>
          </div>
        </div>

        ${paymentData?.amortizationTable ? `
        <div class="section">
          <h2 class="section-title">Tabla de Amortización Completa</h2>
          <table>
            <thead>
              <tr>
                <th>Pago #</th>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Pago Total</th>
                <th>Capital</th>
                <th>Interés</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
              <tr class="highlight">
                <td>0</td>
                <td>${new Date().toLocaleDateString('es-MX')}</td>
                <td><strong>ENGANCHE</strong></td>
                <td>${formatCurrency(downPaymentAmount, investment.currencyFormat)}</td>
                <td>${formatCurrency(downPaymentAmount, investment.currencyFormat)}</td>
                <td>${formatCurrency(0, investment.currencyFormat)}</td>
                <td>${formatCurrency(paymentData.totalFinanced, investment.currencyFormat)}</td>
              </tr>
              ${paymentData.amortizationTable.map(entry => `
                <tr>
                  <td>${entry.paymentNumber}</td>
                  <td>${entry.date}</td>
                  <td>Mensualidad</td>
                  <td>${formatCurrency(entry.paymentAmount, investment.currencyFormat)}</td>
                  <td>${formatCurrency(entry.principalAmount, investment.currencyFormat)}</td>
                  <td>${formatCurrency(entry.interestAmount, investment.currencyFormat)}</td>
                  <td>${formatCurrency(entry.remainingBalance, investment.currencyFormat)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="section">
          <h2 class="section-title">Información de Pago</h2>
          <div class="grid">
            <div class="stat-card">
              <h3>Cuentas Bancarias para Depósito</h3>
              <div style="margin-bottom: 15px;">
                <h4 style="color: #16a34a; margin-bottom: 8px;">FIDECOMISO 697 - SOLO TRANSFERENCIA</h4>
                <p><strong>Banco:</strong> INSTITUCIÓN DE BANCA MÚLTIPLE, GRUPO FINANCIERO VE POR MÁS</p>
                <p><strong>No. de Cuenta:</strong> 0000070758</p>
                <p><strong>CLABE:</strong> 113180000007017586</p>
              </div>
              <div style="margin-bottom: 15px;">
                <h4 style="color: #16a34a; margin-bottom: 8px;">BBVA MÉXICO - TRANSFERENCIA Y EFECTIVO</h4>
                <p><strong>No. de Cuenta:</strong> 0118891737</p>
                <p><strong>CLABE:</strong> 012910001188917370</p>
              </div>
              <div style="margin-bottom: 15px;">
                <h4 style="color: #16a34a; margin-bottom: 8px;">SANTANDER - TRANSFERENCIA Y EFECTIVO</h4>
                <p><strong>No. de Cuenta:</strong> 65 50166596</p>
                <p><strong>CLABE:</strong> 646017230197572477</p>
              </div>
            </div>
            
            <div class="stat-card">
              <h3>Cuentas en Dólares - CITRUS PATRIMONIAL</h3>
              <p><strong>Banco:</strong> JP MORGAN CHASE BANK N.A.</p>
              <p><strong>No. de Cuenta:</strong> 336794722</p>
              <p><strong>ABA (ACH):</strong> 111000614</p>
              <p><strong>Routing No. (Wire Transfer):</strong> 21000021</p>
            </div>
            
            <div class="stat-card">
              <h3>Pago con Tarjeta</h3>
              <p><strong>Link de Pago:</strong></p>
              <p><a href="https://inversion.citruspatrimonial.com/b/14k9EAdvE78T0N26oN" target="_blank">https://inversion.citruspatrimonial.com/b/14k9EAdvE78T0N26oN</a></p>
              <p><strong>Disponible:</strong> 24/7</p>
              <p><strong>Procesamiento:</strong> Seguro y encriptado</p>
            </div>
            
            <div class="stat-card">
              <h3>Contacto</h3>
              <p><strong>WhatsApp:</strong> +52 55 1000 0615</p>
              <p><strong>Email:</strong> inversiones@citruspatrimonial.mx</p>
              <p><strong>Horario:</strong> Lun-Vie 9:00-18:00</p>
            </div>
            
            <div class="stat-card">
              <h3>Importante</h3>
              <ul>
                <li>• Realizar pagos en fechas programadas</li>
                <li>• Enviar comprobante a inversiones@citruspatrimonial.mx</li>
                <li>• Para efectivo usar solo BBVA o Santander</li>
                <li>• Transferencias internacionales usar cuenta JP Morgan</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="footer">
          <p>© ${new Date().getFullYear()} Cosecha Capital</p>
          <p>Este reporte es generado automáticamente y contiene información confidencial del cliente.</p>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Reporte_Pagos_${(state.investorName || 'Cliente').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setTimeout(() => {
      setState(prev => ({ ...prev, downloadStatus: 'complete' }));
      setTimeout(() => setState(prev => ({ ...prev, downloadStatus: 'idle' })), 2000);
    }, 1000);
  };

  const tabs = [
    {
      id: 'configuration',
      label: 'Configuración',
      icon: <Settings className="w-5 h-5" />,
      description: 'Datos del cliente y parámetros de inversión'
    },
    {
      id: 'downPaymentSchedule',
      label: 'Cronograma Enganche',
      icon: <Calendar className="w-5 h-5" />,
      description: 'Plan de pagos del enganche'
    },
    {
      id: 'amortization',
      label: 'Tabla Amortización',
      icon: <CreditCard className="w-5 h-5" />,
      description: 'Enganche + 48 mensualidades completas'
    },
    {
      id: 'buybackPrices',
      label: 'Precios Recompra',
      icon: <TrendingUp className="w-5 h-5" />,
      description: '30 años de precios de recompra'
    },
    {
      id: 'returnsSimulator',
      label: 'Simulador Rendimientos',
      icon: <BarChart3 className="w-5 h-5" />,
      description: 'Rendimiento operativo y apreciación'
    }
  ];

  const renderTabContent = () => {
    if (!paymentData) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Calculando datos de pago...</p>
            <p className="text-gray-500 text-sm mt-2">Procesando información financiera</p>
          </div>
        </div>
      );
    }

    switch (state.activeTab) {
      case 'configuration':
        return (
          <ConfigurationTab
            state={state}
            onStateUpdate={handleStateUpdate}
            investment={investment}
          />
        );
      case 'downPaymentSchedule':
        return (
          <DownPaymentScheduleTab
            paymentData={paymentData}
            investment={investment}
          />
        );
      case 'amortization':
        return (
          <AmortizationTab
            paymentData={paymentData}
            investment={investment}
          />
        );
      case 'buybackPrices':
        return (
          <BuybackPricesTab
            paymentData={paymentData}
            investment={investment}
          />
        );
      case 'returnsSimulator':
        return (
          <ReturnsSimulatorTab
            paymentData={paymentData}
            investment={investment}
          />
        );
      default:
        return (
          <ConfigurationTab
            state={state}
            onStateUpdate={handleStateUpdate}
            investment={investment}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Calculadora de Pagos</h1>
                <p className="text-gray-600">Planes de pago, amortización y proyecciones de rendimientos</p>
                
                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {!isClientInfoComplete() ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-amber-50 text-amber-700 rounded-lg border border-amber-200">
                <span className="font-medium">Complete la información del cliente para acceder a todas las funciones</span>
              </div>
            </div>
          ) : (
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleStateUpdate({ activeTab: tab.id as any })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    state.activeTab === tab.id
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                  title={tab.description}
                >
                  {tab.icon}
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Download Button - Only visible when client info is complete */}
          {isClientInfoComplete() && (
            <div className="mb-6 flex justify-end">
              <button
                onClick={generateCompleteReport}
                disabled={state.downloadStatus === 'downloading'}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105 ${
                  state.downloadStatus === 'complete' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {state.downloadStatus === 'idle' && (
                  <>
                    <Download className="w-5 h-5" />
                    <span className="font-medium">Descargar Reporte Completo</span>
                  </>
                )}
                {state.downloadStatus === 'downloading' && (
                  <>
                    <FileText className="w-5 h-5 animate-pulse" />
                    <span className="font-medium">Generando Reporte...</span>
                  </>
                )}
                {state.downloadStatus === 'complete' && (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">¡Reporte Descargado!</span>
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Content based on client info completion */}
          {!isClientInfoComplete() ? (
            <ConfigurationTab
              state={state}
              onStateUpdate={handleStateUpdate}
              investment={investment}
            />
          ) : (
            renderTabContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCalculatorMain;